import { useState, useMemo, useEffect } from "react";
import { Link } from "~/components/Link";
import { useRouter } from "next/router";
import { ChevronRight, Search } from "lucide-react";
import { cn } from "~/lib/utils";
import { ScrollArea } from "~/components/ui/scroll-area";
import { getMeasureURL, getReferenceDataURL } from "~/routes";
import type { Measure } from "~/modules/measures/types";
import type { ReferenceData } from "~/modules/reference_data/types";
import { CatalogUserEvents } from "~/modules/analytics/events/catalog";
import { CatalogIcon } from "../CatalogIcon";

// // // //

interface NavLinkProps {
    href: string;
    label: string;
    slug: string;
    isActive?: boolean;
    analyticsLabel: CatalogUserEvents;
}

function NavLink({
    href,
    label,
    slug,
    isActive,
    analyticsLabel,
}: NavLinkProps) {
    return (
        <Link
            href={href}
            analyticsLabel={analyticsLabel}
            analyticsEventProperties={{ slug }}
            className={cn(
                "block min-w-0 w-full truncate pr-2 pl-8 py-1 rounded-md transition-colors text-left text-sm",
                {
                    "bg-slate-200 text-foreground font-medium": isActive,
                    "text-tertiary-foreground hover:bg-slate-200 hover:text-foreground font-normal":
                        !isActive,
                },
            )}
        >
            {label}
        </Link>
    );
}

// // // //

interface CollapsibleSectionProps {
    icon?: React.ReactNode;
    label: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
    forceOpen?: boolean;
    nested?: boolean;
    hideCollapseButton?: boolean;
}

function CollapsibleSection({
    label,
    icon,
    children,
    defaultOpen = false,
    forceOpen = false,
    nested = false,
    hideCollapseButton = false,
}: CollapsibleSectionProps) {
    const [open, setOpen] = useState(defaultOpen);
    useEffect(() => {
        setOpen(defaultOpen);
    }, [defaultOpen]);

    const isOpen = forceOpen || open;

    return (
        <div className="min-w-0 overflow-hidden">
            <button
                onClick={() => {
                    if (hideCollapseButton) return;
                    setOpen(!isOpen);
                }}
                className={cn(
                    "flex w-full min-w-0 items-center gap-2 rounded-md transition-colors hover:bg-slate-200 hover:text-foreground",
                    {
                        "px-2 py-1 text-xs text-tertiary-foreground": nested,
                        "px-2 py-1.5 text-sm text-foreground": !nested,
                    },
                )}
            >
                {!hideCollapseButton && (
                    <ChevronRight
                        className={cn(
                            "h-3.5 w-3.5 shrink-0 text-tertiary-foreground transition-transform duration-200",
                            {
                                "rotate-90": isOpen,
                            },
                        )}
                    />
                )}
                {icon}
                <span
                    className={cn("min-w-0 flex-1 truncate text-left", {
                        "text-sm font-medium": nested,
                        "text-base font-normal": !nested,
                    })}
                >
                    {label}
                </span>
            </button>

            {isOpen && (
                <div className="min-w-0 space-y-0.5 overflow-hidden py-1">
                    {children}
                </div>
            )}
        </div>
    );
}

// // // //

interface CategoryGroup<T> {
    category: string;
    icon: string;
    color: string;
    items: T[];
}

function groupByCategory<
    T extends {
        category: string | null;
        category_order: number;
        item_order: number;
        icon: string;
        color: string;
    },
>(items: T[]): CategoryGroup<T>[] {
    const groups = new Map<
        string,
        { order: number; icon: string; color: string; items: T[] }
    >();
    for (const item of items) {
        if (!item.category) continue;
        const existing = groups.get(item.category);
        if (existing) {
            existing.items.push(item);
        } else {
            groups.set(item.category, {
                order: item.category_order,
                icon: item.icon,
                color: item.color,
                items: [item],
            });
        }
    }

    return Array.from(groups, ([category, { order, icon, color, items }]) => ({
        category,
        order,
        icon,
        color,
        items: items.sort((a, b) => a.item_order - b.item_order),
    })).sort((a, b) => a.order - b.order);
}

// // // //

export interface CatalogOverviewSidebarProps {
    measures: Measure[];
    referenceData: ReferenceData[];
}

export function CatalogOverviewSidebar({
    measures,
    referenceData,
}: CatalogOverviewSidebarProps) {
    const { asPath } = useRouter();
    const [searchQuery, setSearchQuery] = useState("");
    const isSearching = searchQuery.trim().length > 0;

    const filteredMeasures = useMemo(() => {
        if (!isSearching) return measures;
        const q = searchQuery.toLowerCase();
        return measures.filter(
            (m) =>
                m.display_name.toLowerCase().includes(q) ||
                m.name.toLowerCase().includes(q) ||
                m.slug.toLowerCase().includes(q) ||
                (m.category && m.category.toLowerCase().includes(q)),
        );
    }, [measures, searchQuery, isSearching]);

    const measureGroups = useMemo(
        () => groupByCategory(filteredMeasures),
        [filteredMeasures],
    );

    const filteredReferenceData = useMemo(() => {
        if (!isSearching) return referenceData;
        const q = searchQuery.toLowerCase();
        return referenceData.filter(
            (item) =>
                item.name.toLowerCase().includes(q) ||
                item.slug.toLowerCase().includes(q) ||
                item.category.toLowerCase().includes(q),
        );
    }, [referenceData, searchQuery, isSearching]);

    const referenceDataGroups = useMemo(
        () => groupByCategory(filteredReferenceData),
        [filteredReferenceData],
    );

    return (
        <aside className="hidden lg:flex lg:w-64 shrink-0 flex-col select-none h-full overflow-hidden border-r hide-scrollbar">
            <div className="px-3 py-2 border-b shrink-0">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-tertiary-foreground" />
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="w-full rounded-md border border-border bg-card py-1.5 pl-7 pr-2 text-xs placeholder:text-tertiary-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
            </div>

            <ScrollArea className="flex-1 min-h-0 min-w-0 [&_[data-slot=scroll-area-viewport]>div]:!block [&_[data-slot=scroll-area-viewport]>div]:!min-w-0">
                <nav className="flex min-w-0 flex-col gap-1 overflow-hidden pt-2 pb-8 px-2">
                    {/* Metrics — grouped by category */}
                    {filteredMeasures.length > 0 && (
                        <CollapsibleSection
                            label="Metrics"
                            defaultOpen
                            hideCollapseButton
                        >
                            {measureGroups.map((group) =>
                                measureGroups.length === 1 ? (
                                    group.items.map((measure) => (
                                        <NavLink
                                            key={measure.id}
                                            href={getMeasureURL({
                                                measureSlug: measure.slug,
                                            })}
                                            label={measure.display_name}
                                            slug={measure.slug}
                                            analyticsLabel={
                                                CatalogUserEvents.ClickSidebarMetricLink
                                            }
                                            isActive={
                                                getMeasureURL({
                                                    measureSlug: measure.slug,
                                                }) === asPath
                                            }
                                        />
                                    ))
                                ) : (
                                    <CollapsibleSection
                                        key={group.category}
                                        icon={
                                            <CatalogIcon
                                                icon={group.icon}
                                                color={group.color}
                                                size="sm"
                                            />
                                        }
                                        label={group.category}
                                        nested
                                        defaultOpen={group.items.some(
                                            (m) =>
                                                getMeasureURL({
                                                    measureSlug: m.slug,
                                                }) === asPath,
                                        )}
                                        forceOpen={isSearching}
                                    >
                                        {group.items.map((measure) => (
                                            <NavLink
                                                key={measure.id}
                                                href={getMeasureURL({
                                                    measureSlug: measure.slug,
                                                })}
                                                label={measure.display_name}
                                                slug={measure.slug}
                                                analyticsLabel={
                                                    CatalogUserEvents.ClickSidebarMetricLink
                                                }
                                                isActive={
                                                    getMeasureURL({
                                                        measureSlug:
                                                            measure.slug,
                                                    }) === asPath
                                                }
                                            />
                                        ))}
                                    </CollapsibleSection>
                                ),
                            )}
                        </CollapsibleSection>
                    )}

                    {/* Reference Data — grouped by category */}
                    {filteredReferenceData.length > 0 && (
                        <CollapsibleSection
                            label="Reference Data"
                            defaultOpen
                            hideCollapseButton
                        >
                            {referenceDataGroups.map((group) =>
                                referenceDataGroups.length === 1 ? (
                                    group.items.map((item) => (
                                        <NavLink
                                            key={item.id}
                                            href={getReferenceDataURL({
                                                referenceSlug: item.slug,
                                            })}
                                            label={item.name}
                                            slug={item.slug}
                                            analyticsLabel={
                                                CatalogUserEvents.ClickSidebarReferenceDataLink
                                            }
                                            isActive={
                                                getReferenceDataURL({
                                                    referenceSlug: item.slug,
                                                }) === asPath
                                            }
                                        />
                                    ))
                                ) : (
                                    <CollapsibleSection
                                        key={group.category}
                                        icon={
                                            <CatalogIcon
                                                icon={group.icon}
                                                color={group.color}
                                                size="sm"
                                            />
                                        }
                                        label={group.category}
                                        nested
                                        defaultOpen={group.items.some(
                                            (rd) =>
                                                getReferenceDataURL({
                                                    referenceSlug: rd.slug,
                                                }) === asPath,
                                        )}
                                        forceOpen={isSearching}
                                    >
                                        {group.items.map((item) => (
                                            <NavLink
                                                key={item.id}
                                                href={getReferenceDataURL({
                                                    referenceSlug: item.slug,
                                                })}
                                                label={item.name}
                                                slug={item.slug}
                                                analyticsLabel={
                                                    CatalogUserEvents.ClickSidebarReferenceDataLink
                                                }
                                                isActive={
                                                    getReferenceDataURL({
                                                        referenceSlug:
                                                            item.slug,
                                                    }) === asPath
                                                }
                                            />
                                        ))}
                                    </CollapsibleSection>
                                ),
                            )}
                        </CollapsibleSection>
                    )}

                    {/* Empty search state */}
                    {isSearching &&
                        filteredMeasures.length === 0 &&
                        filteredReferenceData.length === 0 && (
                            <p className="px-4 py-3 text-xs text-tertiary-foreground">
                                No results for &ldquo;{searchQuery}&rdquo;
                            </p>
                        )}
                </nav>
            </ScrollArea>
        </aside>
    );
}
